<template>
	<div class="data-chip-content" :class="{ 'is-html': isRichTextEnabled, 'is-focused': isFocused, 'is-selected': isSelected }">
		<div class="data-icons">
			<div class="data-chip-icon-container-container" v-if="!isRichTextEnabled">
				<IntegrationIcon :integrationName="serviceSlug" :size="15" />
			</div>
			<div class="data-chip-icon-container-container" v-if="!isRichTextEnabled">
				<DataTypeIcon :itemValue="itemValue" :size="15" />
			</div>
		</div>
		<div class="chip-source" v-if="isSource || isSampleAndSource">
			{{ chipSource }}
		</div>
		<div class="chip-sample" v-if="isSample || isSampleAndSource" v-html="cleanHtml" />
		<span v-if="isAutofilled" class="chip-autofill" title="Autofilled by AI">
			<SparklesIcon />
			<span class="reject-autofill" @click="$emit('autofill-rejected')"> <XIcon /> </span
		></span>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { sanitizeHtml } from '@/utils/helpers';
import { escapeNonValidHTMLTags } from '@/utils/helpers';
import XIcon from '@/components/common/RichTextBox/assets/x.svg';
import SparklesIcon from '@/components/common/RichTextBox/assets/sparkles.svg';

export default {
	name: 'DataChipContent',
	components: {
		XIcon,
		SparklesIcon,
	},
	props: {
		chipSample: { type: String },
		chipSource: { type: String },
		serviceSlug: { type: String },
		nodeInfo: { type: Object },
		config: { type: Object },
		itemValue: { type: [String, Number, Array, Object, Boolean] },
		isTableParam: { type: Boolean },
		isFocused: { type: Boolean },
		isSelected: { type: Boolean },
		isAutofilled: { type: Boolean, default: false },
	},
	computed: {
		...mapGetters(['richText/insertedDataDisplayMode', 'richText/insertedDataDisplayModeMap']),
		isSource() {
			return this['richText/insertedDataDisplayMode'] === this['richText/insertedDataDisplayModeMap'].SOURCE.id;
		},
		isSample() {
			return this['richText/insertedDataDisplayMode'] === this['richText/insertedDataDisplayModeMap'].SAMPLE.id;
		},
		isSampleAndSource() {
			return this['richText/insertedDataDisplayMode'] === this['richText/insertedDataDisplayModeMap'].SAMPLE_AND_SOURCE.id;
		},

		isRichTextEnabled() {
			return this.config.isRichTextEnabled;
		},
		encodedChipContent() {
			return escapeNonValidHTMLTags(this.chipSample);
		},
		cleanHtml() {
			return sanitizeHtml(this.encodedChipContent);
		},
	},
};
</script>

<style scoped lang="scss">
$chip-border-radius: 3px;

.data-chip-content {
	display: inline;
	border-radius: $chip-border-radius;

	.data-icons {
		display: inline-flex;
		align-items: center;

		.data-chip-icon-container-container {
			display: inline-flex;
			z-index: 9;

			::v-deep {
				.integration-name,
				.data-type-icon {
					margin: 0;
					border: none;
					border-radius: 0;
					box-shadow: none;
					background-color: #ffffff;
				}
			}

			&:first-child {
				::v-deep {
					.integration-name {
						margin-right: 1px;
						border-top-left-radius: 2px;
						border-bottom-left-radius: 2px;
						transform: translate(1px, -1px);
					}
				}
			}
			&:not(:first-child) {
				margin: -1px 0 0 1px;
			}

			::v-deep {
				svg {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
				}
			}
		}
	}
	.chip-source {
		display: inline-flex;
		padding: 0 2px;
		transform: translate(-2px, -1px);
		border-radius: $chip-border-radius;
		background-color: var(--border-color);
		color: var(--primary-font-color);
		font-size: 12px;
		line-height: 15px;
		font-weight: normal;
		user-select: none;
	}
	.chip-sample {
		display: inline-flex;
		margin: 0 4px;
		z-index: -1;
		// user-select: none; // we removed this so when it is part of a selected text it can be indicated (with background color)
	}

	.chip-autofill {
		user-select: none;
		display: inline-flex;
		font-size: 12px;
		line-height: 1;
		.reject-autofill {
			padding: 0 4px;
			font-weight: bold;
			border-top-right-radius: 5px;
			border-bottom-right-radius: 5px;
			&:hover {
				filter: invert(67%) sepia(89%) saturate(7492%) hue-rotate(346deg) brightness(84%) contrast(146%);
			}
		}
	}

	&.is-html .chip-source {
		transform: translate(0, -1px);
		padding: 1px;
	}

	@mixin chip-design($bg-color, $box-shadow) {
		&,
		::v-deep p {
			/* included both on the chip (&) and the inner paragraphs (::v-deep p) */
			background-color: $bg-color;
			box-shadow: $box-shadow;
			transition: 0.5s;

			.chip-source {
				background-color: $bg-color;
				transition: 0.5s;
			}
		}

		::v-deep p {
			&:first-child {
				display: inline; // so that first & last paragraphs are inline with previous & following text

				&::after {
					content: '\A';
					white-space: pre;
				}
			}
			&:last-child {
				display: inline; // so that first & last paragraphs are inline with previous & following text
			}
		}
	}

	&:hover {
		@include chip-design(#e1e3ea, none);
	}
	&:not(:hover) {
		@include chip-design(var(--soft-grey-background), none);
	}
	&.is-focused {
		@include chip-design(#b3d7fd, inset 0 0 0 1px var(--primary-color));
	}
	&.is-selected {
		.chip-sample {
			user-select: none; // remove "selection" when chip is open

			background: #d0d8f2;
			transition: 0.2s;
		}
	}
}
</style>
